/**
 * Контракт вывода модели-писателя. docs/phase-2/03-generation.md §7, §13.2.
 *
 * Шов CLI↔API проходит ровно по WriterOutput: слева — то, что порождает текст (сессия CLI
 * в подфазе, prompt/*+llm/* в Б3), справа — pipeline/build и submit, и они не меняются.
 * Поэтому схема живёт в contracts, а не в сервисе: её потребители — bin/submit.ts подфазы,
 * тесты, сравнение CLI и API на одном корпусе и output_config.format запроса к модели.
 *
 * Чего в схеме намеренно НЕТ:
 *  - entities/offset/length — модель их не считает (§8: пишет диалектом разметки);
 *  - text — он выводится разбором source_markup, второго источника текста нет;
 *  - link_preview_options, show_caption_above_media, has_spoiler, method — выводятся
 *    из формата (Р10); способ отправки бэкенд выводит сам — `freezeAuthored`;
 *  - длина, хэш, topic_id, brand — знание сервиса, а не модели;
 *  - visual — РЕШЕНИЕ О КАРТИНКЕ. Оно выносится отдельным вызовом ПОСЛЕ текста
 *    (docs/phase-2/08-visuals.md §2.3), и его здесь нет намеренно, а не по недоделке:
 *    поле в строгой схеме вывода модель читает не как «можно», а как задание — «заполни».
 *    Пока слот стоит в схеме, любой механизм честности ниже по течению спорит с формой,
 *    которую модели уже выдали. Прежний комментарий («заполняется только у форматов с
 *    method: sendPhoto») к тому же был неправдой с 06.09.2026: `method` ушёл из фронтматтера
 *    формата вместе с rich-транспортом, и оправдать слот стало нечем вовсе.
 */

import * as z from 'zod';

export const FormatChoiceSchema = z.strictObject({
  format: z.string().regex(/^[a-z][a-z0-9-]{1,63}$/),
  /** Одна строка. Ровно то, что 3.2 называет «обоснование формата одной строкой». */
  format_rationale: z.string().min(10).max(280),
});
export type FormatChoice = z.output<typeof FormatChoiceSchema>;

export const WriterOutputSchema = z.strictObject({
  /**
   * Пост РАЗМЕТКОЙ ИСХОДНИКА (docs/ui-design.md §6.5), не {text, entities}.
   * max(20000) — не лимит Telegram, а предохранитель от разогнавшегося ответа; настоящие
   * лимиты считает checkLimits по канонической форме, и заменять её схемой нельзя.
   */
  source_markup: z.string().min(1).max(20000),
  /**
   * Одна строка: чем эта версия отличается от предыдущих.
   * В базу НЕ попадает (поля для неё в теле intake нет). Живёт ровно два места:
   * лог выбора версии и промпт следующего вызова — «версия 3, угол не такой, как в 1 и 2».
   */
  angle: z.string().min(5).max(200),
  /**
   * «Опубликуй ровно это». Поднимает флаг САМА МОДЕЛЬ, распознав режим по задаче (правка 6
   * от 2026-09-08, §6.3): человек вставил готовый текст и попросил его опубликовать.
   *
   * ЧТО ОН СНИМАЕТ. Обещание «выведет ровно тот текст» — ложь, пока работают три механизма:
   * выбор формата с его коридором, переписывающий проход живого текста и безусловная
   * нормализация кавычек. При поднятом флаге нулевой вызов «выбери формат» пропускается,
   * фразы про коридор в бриф не печатаются, гейт живого текста РАПОРТУЕТ, но не
   * переписывает, а кавычки остаются как есть — иначе вставленный текст с типографскими
   * кавычками вернётся изменённым, и человек прочитает это как «бот проигнорировал».
   *
   * ЧТИТСЯ ТОЛЬКО КОГДА У ЗАДАЧИ ЕСТЬ КОНТЕКСТ (`GenerationTaskSchema.context !== null`).
   * Это не перестраховка: без такого условия ОБЫЧНЫЙ прогон — тот, где текст пишет модель,
   * а не человек, — мог бы поднять флаг и тем самым обойти гейт живого текста, то есть
   * отключить себе проверку качества по собственному решению. Гейт для машинного текста и
   * есть причина, по которой этот сервис существует; отказ от него законен ровно тогда,
   * когда текст принёс человек.
   *
   * Умолчание `false` (а не `.optional()`): «дословно не просили» — это полноценное
   * значение, и ветвление ниже по течению не должно различать `undefined` и `false`.
   */
  verbatim: z.boolean().default(false),
});
export type WriterOutput = z.output<typeof WriterOutputSchema>;

/**
 * Та же схема как JSON Schema для output_config.format строгого структурированного вывода
 * (§13.2: один объект, два способа его получить). strictObject даёт
 * additionalProperties: false — это утверждение теста, а не комментарий.
 */
export const WRITER_OUTPUT_JSON_SCHEMA = z.toJSONSchema(WriterOutputSchema, {
  target: 'draft-2020-12',
});

/**
 * То же для вызова «выбери формат» (§6.1, вызов 0). Живёт здесь, а не в сервисе, по той же
 * причине, что и соседняя константа: схема ответа и схема запроса обязаны быть одним
 * объектом, иначе строгий вывод и разбор ответа разъедутся молча.
 */
export const FORMAT_CHOICE_JSON_SCHEMA = z.toJSONSchema(FormatChoiceSchema, {
  target: 'draft-2020-12',
});
